import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';

import { SearchIcon } from '../../shared/ui/icons';
import { Input } from '../../shared/ui/input';
import { Page, PageHeader } from '../../shared/ui/layout';
import { ListFooter } from '../../shared/ui/list-footer';
import type { IColumn } from '../../shared/ui/table';
import { EmptyState, ErrorState, LoadingState, NoMatchState } from '../../shared/ui/states';
import { Time } from '../../shared/ui/time';
import {
  FolderPath,
  folderKeys,
  isFolderGone,
  listFolders,
  moveItems,
} from '../../entities/folder';
import { FolderCreate } from '../../features/folder-create';
import { FolderBrowser } from '../../widgets/folder-browser';
import {
  ListFilterBar,
  exactCounts,
  narrowRows,
  type IListFilters,
  type IListView,
} from '../../features/list-filters';
import {} from '../../entities/transform';
import {
  KIND_LABEL,
  libraryKeys,
  listLibraryIn,
  type TLibraryItem,
  type TLibraryUse,
} from '../../entities/library';
import { LibraryCreate } from '../../features/library-create';

interface IRow {
  item: TLibraryItem;
  flows: TLibraryUse[];
}

const liveFlows = (row: IRow) => row.flows.filter((use) => !use.onlyInDraft);

export const LIBRARY_VIEWS: IListView<IRow>[] = [
  { value: 'all', label: 'Everything', keeps: () => true },
  { value: 'code', label: 'Code', keeps: (row) => row.item.kind === 'code' },
  { value: 'prompt', label: 'Prompts', keeps: (row) => row.item.kind === 'prompt' },
  { value: 'running', label: 'Used in a flow', keeps: (row) => row.flows.length > 0 },
  { value: 'unpinned', label: 'Not used yet', keeps: (row) => row.flows.length === 0 },
];

// A just-written item is unused, so a narrower default hides it from its own list.
export const DEFAULT_LIBRARY_VIEW = 'all';

const MAX_NAMES = 2;

// "2 · Stripe production, Stripe → Ledger mirror"; "1 · Calendly → CRM sync, in its draft".
const usedInText = (row: IRow): string => {
  const names = row.flows.slice(0, MAX_NAMES).map((use) => use.flowName);
  const more = row.flows.length - names.length;
  const drafted = row.flows.length === 1 && row.flows[0].onlyInDraft;
  const onlyDrafts = row.flows.every((use) => use.onlyInDraft);
  return (
    `${row.flows.length.toLocaleString('en-US')} · ${names.join(', ')}` +
    (more > 0 ? ` and ${more.toLocaleString('en-US')} more` : '') +
    (drafted ? ', in its draft' : onlyDrafts ? ', in their drafts' : '')
  );
};

const behindText = (row: IRow): string | null => {
  const latest = row.item.latestVersion;
  const behind = liveFlows(row).filter(
    (use) => use.pinnedVersion !== null && use.pinnedVersion < latest,
  );
  if (behind.length === 0) return null;
  const versions = [...new Set(behind.map((use) => use.pinnedVersion))];
  const on = versions.length === 1 ? `v${versions[0]}` : 'older versions';
  return `${behind.length === 1 ? '1 flow' : `${behind.length} flows`} still on ${on}`;
};

// The kind leads the second line: a column of its own would need a width token of its own.
const detailText = (row: IRow): string => {
  const reads =
    row.item.kind === 'prompt' && row.item.variables.length > 0
      ? `reads ${row.item.variables.map((name) => `{{vars.${name}}}`).join(', ')}`
      : null;
  return [KIND_LABEL[row.item.kind], row.item.description, reads].filter(Boolean).join(' · ');
};

const columns = (now: number, timeZone?: string, searching = false): IColumn<IRow>[] => [
  {
    key: 'name',
    header: 'Name',
    width: 'grow',
    ui: true,
    render: (row) => (
      <span className="flex min-w-0 flex-col">
        <span className="truncate font-mono text-primary">{row.item.name}</span>
        {searching ? (
          <FolderPath scope="library" path={row.item.folderPath} />
        ) : (
          <span className="truncate text-meta text-muted">{detailText(row)}</span>
        )}
      </span>
    ),
  },
  {
    key: 'latest',
    header: 'Latest',
    width: 'w-col-try',
    render: (row) => (row.item.latestVersion > 0 ? `v${row.item.latestVersion}` : '—'),
  },
  {
    key: 'usedIn',
    header: 'Used in flows',
    width: 'grow',
    ui: true,
    render: (row) => {
      const behind = behindText(row);
      return (
        <span className="flex min-w-0 flex-col">
          {row.flows.length === 0 ? (
            <span className="text-faint">None yet</span>
          ) : (
            <span className="truncate" title={row.flows.map((use) => use.flowName).join(', ')}>
              {usedInText(row)}
            </span>
          )}
          {behind && <span className="truncate font-mono text-micro text-muted">{behind}</span>}
        </span>
      );
    },
  },
  {
    key: 'edited',
    header: 'Edited',
    width: 'w-col-time',
    render: (row) => <Time iso={row.item.updatedAt} now={now} timeZone={timeZone} />,
  },
];

interface ILibraryPageProps {
  filters: IListFilters;
  onFilters: (next: IListFilters) => void;
  /** The open folder (`fld_…`); absent is the top level. */
  folder?: string;
  onFolder: (folder: string | null) => void;
  /** A name search, across every folder. */
  q?: string;
  onSearch: (q: string | undefined) => void;
  onOpen: (item: TLibraryItem) => void;
  timeZone?: string;
}

export const LibraryPage = ({
  filters,
  onFilters,
  folder: folderId,
  onFolder,
  q,
  onSearch,
  onOpen,
  timeZone,
}: ILibraryPageProps) => {
  const where = q ? { q } : { folderId: folderId ?? 'root' };
  const [items, folders] = useQueries({
    queries: [
      { queryKey: libraryKeys.listIn(where), queryFn: () => listLibraryIn(where) },
      { queryKey: folderKeys.list('library'), queryFn: () => listFolders('library') },
    ],
  });
  const folder =
    folderId && !q ? (folders.data?.data.find((entry) => entry.id === folderId) ?? null) : null;

  // An item made while a folder is open is filed there before its editor opens.
  const created = (item: TLibraryItem) => {
    if (!folder) return onOpen(item);
    void moveItems('library', [item.id], folder.id).finally(() => onOpen(item));
  };

  const now = useMemo(() => Date.now(), [items.dataUpdatedAt]);

  const all: IRow[] = (items.data?.data ?? []).map((item) => ({ item, flows: item.flows }));

  const rows = narrowRows(all, LIBRARY_VIEWS, filters);
  const libraryEmpty = !q && !folderId && folders.isSuccess && folders.data.data.length === 0;

  const empty =
    all.length > 0 || q ? (
      <NoMatchState
        onReset={() => (q ? onSearch(undefined) : onFilters({ view: DEFAULT_LIBRARY_VIEW }))}
      />
    ) : libraryEmpty ? (
      <EmptyState
        title="The Library is empty"
        description="Code is a function that takes the event and returns the new one; a prompt is the messages an AI step sends. Either runs only where a step in a flow pins one of its versions, so writing it changes nothing until a step does."
      />
    ) : undefined;

  return (
    <>
      <PageHeader
        width="full"
        title="Library"
        description="Code and prompts that steps pin by version: write once, use in any flow."
        actions={
          <>
            <FolderCreate scope="library" parent={folder} />
            <LibraryCreate onCreated={created} />
          </>
        }
      />

      <ListFilterBar
        views={LIBRARY_VIEWS}
        filters={filters}
        onChange={onFilters}
        counts={items.data ? exactCounts(all, LIBRARY_VIEWS, items.data.total) : undefined}
        label="What to show"
        trailing={
          <Input
            size="sm"
            type="search"
            aria-label="Find in the Library"
            placeholder="Find in every folder"
            leading={<SearchIcon size={13} />}
            className="ml-auto w-search"
            value={q ?? ''}
            onChange={(event) => onSearch(event.target.value || undefined)}
          />
        }
      />

      <Page width="full">
        <FolderBrowser
          scope="library"
          noun="item"
          folderId={q ? null : (folderId ?? null)}
          onFolder={onFolder}
          searching={Boolean(q)}
          rows={rows}
          ready={items.isSuccess}
          columns={columns(now, timeZone, Boolean(q))}
          rowKey={(row) => row.item.id}
          rowLabel={(row) => row.item.name}
          rowPath={(row) => row.item.folderPath}
          onRowClick={(row) => onOpen(row.item)}
          tall
          layoutId="library"
          empty={empty}
          footer={rows.length > 0 && <ListFooter shown={rows.length} total={items.data?.total} />}
        >
          {items.isError && !isFolderGone(items.error) && (
            <ErrorState error={items.error} onRetry={() => void items.refetch()} />
          )}
          {items.isPending && <LoadingState rows={6} />}
        </FolderBrowser>
      </Page>
    </>
  );
};
